import type {FC, UIEvent} from 'react';
import {useInfiniteQuery} from '@tanstack/react-query';
import {
  Box,
  IconButton,
  Popover,
  Stack,
  Typography,
} from '@wellstone-solutions/web';
import {recoveryDatesQueryOptions} from 'queries';
import {LoadingIndicator} from 'components/shared/LoadingIndicator';
import {RecoveryHistoryItem} from './RecoveryHistoryItem';

interface RecoveryHistoryPopoverProps {
  userId: string;
  memberName?: string;
  anchorEl: HTMLElement;
  onClose: () => void;
}

export const RecoveryHistoryPopover: FC<RecoveryHistoryPopoverProps> = ({
  userId,
  memberName,
  anchorEl,
  onClose,
}) => {
  const historyQuery = useInfiniteQuery(
    recoveryDatesQueryOptions.infiniteList({userId}),
  );

  const firstName = memberName?.split(' ')[0];

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const {scrollTop, scrollHeight, clientHeight} = e.currentTarget;
    if (
      scrollHeight - scrollTop <= clientHeight * 1.5 &&
      !historyQuery.isFetchingNextPage &&
      historyQuery.hasNextPage
    ) {
      historyQuery.fetchNextPage();
    }
  };

  return (
    <Popover
      open
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
      transformOrigin={{vertical: 'top', horizontal: 'right'}}
      slotProps={{paper: {sx: styles.paper}}}>
      <Stack direction="row" alignItems="center" sx={styles.header}>
        <Typography sx={styles.title}>Recovery Counter history</Typography>
        <IconButton
          icon="Xmark"
          iconProps={{name: 'Xmark', size: 18}}
          size="small"
          aria-label="Close"
          onClick={onClose}
        />
      </Stack>

      <Typography variant="body2" sx={styles.subtitle}>
        Each time {firstName ?? 'the member'} sets or adjusts their recovery
        date, newest first.
      </Typography>

      <Box
        sx={styles.list}
        onScroll={handleScroll}
        data-testid="recovery-history-popover">
        {historyQuery.isLoading && <LoadingIndicator />}

        {historyQuery.data?.pages.map((page, pageNumber) => (
          <Box key={pageNumber}>
            {page.data.results.map((recoveryDate) => (
              <RecoveryHistoryItem
                key={recoveryDate.id}
                recoveryDate={recoveryDate}
              />
            ))}
          </Box>
        ))}

        {historyQuery.isFetchingNextPage && <LoadingIndicator size={24} />}

        {!historyQuery.hasNextPage && !historyQuery.isLoading && (
          <Typography
            variant="caption"
            sx={{display: 'block', textAlign: 'center', mt: 1}}>
            You've reached the end of the list
          </Typography>
        )}
      </Box>
    </Popover>
  );
};

const styles = {
  paper: {
    width: 368,
    maxWidth: '90vw',
    borderRadius: 2,
    padding: 2,
  },
  header: {
    marginBottom: 0.5,
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    flex: 1,
  },
  subtitle: {
    marginBottom: 1,
  },
  list: {
    maxHeight: 224,
    overflowY: 'auto',
  },
};
